import React, { useMemo, useRef } from 'react';

import styled from '@emotion/styled';

interface ExcelPresentationTableRawProps {
  rawTableData?: Map<string, any>[];
}

const ExcelPresentationTableRaw: React.FC<ExcelPresentationTableRawProps> =
  React.memo(({ rawTableData }) => {
    const headers = useMemo(
      () => Object.keys(rawTableData?.[0] || {}),
      [rawTableData]
    );

    const dataObj = useMemo(() => {
      const map = new Map();
      rawTableData?.forEach((item, index) => map.set(index, item));
      return map;
    }, [rawTableData]);

    const gridRef = useRef<HTMLDivElement>(null);

    return (
      <TableContainer ref={gridRef} data-table-container>
        {dataObj && (
          <GridBorder>
            {headers && headers.length > 0 && (
              <TableHeader size={headers.length}>
                <TableCellSerialNo className='header' key='serial'>
                  <em> S/N</em>
                </TableCellSerialNo>
                {headers.map((header, index) => (
                  <TableCell key={`${index}`} className='header'>
                    {header}
                  </TableCell>
                ))}
                <TableRowToolbox className='header'></TableRowToolbox>
              </TableHeader>
            )}
            <TableBody>
              {dataObj &&
                Array.from(dataObj).map(([key, value]) => (
                  <TableBodyRow size={headers.length} key={key}>
                    {/* Row Number */}
                    <TableCellSerialNo key='serial'>
                      {key + 1}
                    </TableCellSerialNo>

                    {/* The actual table row data */}
                    {Object.values(value).map((cellValue, index) => (
                      <TableCell key={`${index}`}>
                        {String(cellValue)}
                      </TableCell>
                    ))}
                  </TableBodyRow>
                ))}
            </TableBody>
          </GridBorder>
        )}
      </TableContainer>
    );
  });

export default ExcelPresentationTableRaw;

const TableContainer = styled.div`
  position: relative;
  width: calc(747px - 36px);
  max-height: 360px;
  border-radius: 4px;
  overflow: auto;
`;

const GridBorder = styled.div`
  min-width: max-content;
  display: flex;
  flex-direction: column;
`;

interface TableHeaderProps {
  size: number;
}

const TableHeader = styled.div<TableHeaderProps>`
  border-bottom: 1px solid ${({ theme }) => theme.palette.borderColor};
  background-color: white;
  display: grid;
  grid-template-columns: 54px repeat(${({ size }) => size}, 1fr) 32px;
  min-width: max-content;
  font-size: 14px;
  font-weight: 500;
  color: #5c5963;

  position: sticky;
  top: 0px;
  z-index: 1500;
`;

interface TableBodyProps {
  // size: number;
}

const TableBody = styled.div<TableBodyProps>`
  background-color: white;
  min-width: max-content;
`;

const TableCell = styled.div`
  display: flex;
  padding: 8px;
  width: 200px;
  font-size: 14px;
  font-weight: 500;
  color: #5c5963;
  align-items: center;
  word-break: break-word;

  &.header {
    font-weight: bold;
    align-items: center;
    cursor: pointer;
  }
`;

const TableCellSerialNo = styled.div`
  display: flex;
  padding: 8px;
  position: sticky;
  left: 0;
  z-index: 2;
  background-color: white;
  align-items: center;
  justify-content: center;

  &.header {
    font-weight: bold;
    background-color: white;
    z-index: 3;
  }
`;

const TableRowToolbox = styled.div`
  display: flex;
  gap: 8px;
  background-color: #fffbfd7b;

  position: sticky;
  right: 0;

  &.header {
    opacity: 0;
  }
`;

interface TableBodyRowProps {
  size: number;
}

const TableBodyRow = styled.div<TableBodyRowProps>`
  display: grid;
  grid-template-columns: 54px repeat(${({ size }) => size}, 1fr) 32px;
  min-width: max-content;
  position: relative;
  z-index: 1;

  &:hover {
    background-color: #f9fafb;
    z-index: 999;
  }

  &:last-child {
    border-bottom: none;
  }
`;
